import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CoachingEnquiryModal = ({ isOpen, onClose, selectedCoaching }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    coachingType: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [modalKey, setModalKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update coaching type when modal opens with selectedCoaching
  useEffect(() => {
    if (isOpen) {
      setModalKey(prev => prev + 1);
      if (selectedCoaching) {
        setFormData(prev => ({
          ...prev,
          coachingType: selectedCoaching
        }));
      }
    }
  }, [isOpen, selectedCoaching]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/coaching-enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
        
        // Reset form after 3 seconds and close modal
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            coachingType: '',
            message: ''
          });
          onClose();
        }, 3000);
      } else {
        throw new Error('Failed to submit enquiry');
      }
    } catch (error) {
      console.error('Coaching enquiry error:', error);
      alert('⚠️ There was an error submitting your enquiry. Please try again or contact us directly at info@spacehash.club.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="coaching-enquiry-modal">
        <DialogHeader>
          <DialogTitle>Coaching Enquiry</DialogTitle>
          <DialogDescription>
            Fill in your details and we'll get back to you shortly
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="confirmation-message">
            <div className="confirmation-icon">✓</div>
            <h3>Enquiry Submitted!</h3>
            <p>Thank you for your interest. We've received your enquiry and will contact you soon.</p>
            <p className="confirmation-note">A confirmation email has been sent to {formData.email} and our team at info@spacehash.club</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="enquiry-form">
            <div className="form-group">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter your full name"
                required
              />
            </div>

            <div className="form-group">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="your.email@example.com"
                required
              />
            </div>

            <div className="form-group">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+91 XXXXX XXXXX"
                required
              />
            </div>

            <div className="form-group">
              <Label htmlFor="coachingType">Coaching Interest *</Label>
              <Select 
                key={modalKey}
                value={formData.coachingType} 
                onValueChange={(value) => handleChange('coachingType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select coaching type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Private Lessons">Private Lessons</SelectItem>
                  <SelectItem value="Group Clinics">Group Clinics</SelectItem>
                  <SelectItem value="Corporate Programs">Corporate Programs</SelectItem>
                  <SelectItem value="General Enquiry">General Enquiry</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label htmlFor="message">Message (Optional)</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                placeholder="Tell us about your experience level, goals, or any questions..."
                rows={4}
              />
            </div>

            <Button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CoachingEnquiryModal;
